
function isTestEnv() {
    return (
        "odendo" in window &&
        "labels" in window.odendo &&
        window.odendo.labels.testEnvironment === true
    );
}

function getLabel() {
    try {
        if (window == null) throw new Error("Window is not defined in scope");

        if (!("odendo" in window) || window.odendo == null)
            throw new Error("Odendo is not defined in scope");

        if (window.odendo.labels == null)
            throw new Error("Labels is not defined in scope");

        if (window.odendo.labels == null)
            throw new Error("Labels is not defined in scope");

        return window.odendo.labels.get();
    } catch (e) {
        console.error(e);
        return undefined;
    }
}

function testEnvironmentLabel() {
    const obj = extend(createTestProxy());
    (window as any).labelsObj = obj;
    return obj;
}

const isProxy = Symbol("isProxy");

function createTestProxy() {
    return new Proxy(
        {},
        {
            get(_, property): LabelDictionary | string | true {
                if (property === isProxy) return true;
                const labels = createProxy(getLabel() ?? {});
                if (labels == null) console.warn("Using labels before defined");
                return getPropertyFromTarget(labels ?? {}, property);
            },
        }
    );
}

interface OwnerInfo {
    parent?: OwnerInfo;
    name: string;
    fullPath: string;
}

function createProxy(lbl: LabelDictionary, ownerInfo?: OwnerInfo) {
    return new Proxy(lbl, {
        get(target, property): LabelDictionary | string | true {
            if (property === isProxy) return true;
            return getPropertyFromTarget(target, property, ownerInfo);
        },
    });
}

const objectSystemKeys = [
    // vue keys
    "__v_isRef",
    "__ob__",
    "_vm",
    "__v_raw",
    "__v_skip",
    "dep",
    "depend",
    "getters",
    "state",
    "__v_isReadonly",
    "__v_isReactive",
    "render",
    "effect",
    "expression",
    "_isVue",
    "__isVue",
    "$options",
    "props",
    "vuex",
    "currentRoute",
    "toJSON",
];

function getPropertyFromTarget(
    target: LabelDictionary,
    property: string | symbol,
    ownerInfo?: OwnerInfo
): any {
    if (property in target) {
        const value = target[property as any];
        if (
            typeof value === "object" &&
            value != null &&
            isTestEnv() &&
            value[isProxy] !== true
        ) {
            return createProxy(value);
        }
        return value;
    }
    if (typeof property === "string" && objectSystemKeys.includes(property)) {
        return target[property];
    }
    if (property === Symbol.toPrimitive) {
        return (hint: string) => {
            console.warn(
                "Tried to use labels object to make primitive " + hint + " type"
            );
            return "";
        };
    }
    if (typeof property === "symbol") {
        return target[property as any];
    }
    const prePath = ownerInfo != null ? ownerInfo.fullPath + "." : "";
    console.warn(
        `Created proxy object for property ${typeof property} ${prePath}${property.toString()}`
    );
    return createProxy(
        {},
        {
            fullPath: getFullPath(ownerInfo) + "." + property.toString(),
            name: property.toString(),
            parent: ownerInfo,
        }
    );
}

function getFullPath(ownerInfo: OwnerInfo | undefined): string {
    if (ownerInfo == null) return "";
    const parentStrings = getFullPath(ownerInfo.parent);
    if (parentStrings.length > 0) return parentStrings + "." + ownerInfo.name;
    return ownerInfo.name;
}

function extend(lbl: LabelDictionary): LabelDictionaryObject {
    lbl.get = function (key: string) {
        const keys = key.split(".");
        let v: undefined | null | LabelDictionary = this;
        for (const key of keys) {
            v = v[key];
            if (v == null) return undefined;
        }
        return v;
    };
    return lbl as LabelDictionaryObject;
}

function wrap(lbl: LabelDictionary): LabelDictionaryObject {
    return createProxy(extend(lbl)) as LabelDictionaryObject;
}

export type LabelDictionaryValue = string | LabelDictionary;
export type LabelDictionary = {
    [key: string]: /*LabelDictionary | string*/ any;
};

export type LabelDictionaryObject = LabelDictionary & {
    get(key: string): any;
};

function createLabels() {
    if (isTestEnv()) {
        console.debug("Initialized testEnvironment:true labels");
        return testEnvironmentLabel();
    } else {
        const labels = wrap(getLabel() ?? {});
        Object.freeze(labels);
        return labels;
    }
}

const labels = createLabels();

export default labels;
